import type { CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { DisplayItem } from "./scene/displayList";
import type { SceneOverlay, VectorEditOverlay } from "./overlayTypes";

export type RenderMode = "full" | "camera";

export type DisplayRenderClass = "tileable" | "liveBackdrop" | "liveRaster" | "dynamic";

export type OverlaySceneSignatureInput = Pick<
  SceneOverlay,
  | "activePageId"
  | "previews"
  | "hiddenNodeIds"
  | "hiddenNodeIdsPreserveOverlayLookup"
  | "draftShape"
  | "draftVectorNetwork"
  | "draftComponentInstance"
  | "textPreviews"
> & Pick<
  VectorEditOverlay,
  | "vertexPreview"
  | "handlePreview"
  | "edgeBendPreview"
  | "vectorTopologyPreview"
>;

export interface SceneCompositionKey {
  document: CurvaDocument;
  revision: number;
  activePageId?: string;
  sceneSignature: string;
  tileVariantKey: string;
}

export function draftVectorNetworkSignature(node: SceneNode): string {
  if (node.type !== "vectorNetwork") {
    return `${node.id}:${node.bounds.x},${node.bounds.y},${node.bounds.width},${node.bounds.height}`;
  }

  const lastVertex = node.network.vertices.at(-1);
  return [
    node.id,
    node.bounds.x,
    node.bounds.y,
    node.bounds.width,
    node.bounds.height,
    node.network.vertices.length,
    node.network.edges.length,
    lastVertex?.x ?? 0,
    lastVertex?.y ?? 0,
  ].join(",");
}

export function overlaySceneSignature(overlay: OverlaySceneSignatureInput, documentRevision: number): string {
  const movePreviews = overlay.previews
    .filter(preview => preview.nodeId !== "__marquee__" && preview.mode !== "copy")
    .map(preview => `${preview.nodeId}:${preview.bounds.x},${preview.bounds.y},${preview.bounds.width},${preview.bounds.height},${preview.rotation ?? ""}`)
    .join("|");
  const copyPreviews = overlay.previews
    .filter(preview => preview.nodeId !== "__marquee__" && preview.mode === "copy")
    .map(preview => `${preview.nodeId}:${preview.bounds.x},${preview.bounds.y},${preview.bounds.width},${preview.bounds.height},${preview.rotation ?? ""}`)
    .join("|");
  const vectorPreview = overlay.vertexPreview
    ? [
        overlay.vertexPreview.nodeId,
        // Serialize every moved vertex, not just the active one. Tools like the
        // sculpt brush move many vertices at once while the "active" vertex may
        // stay put; keying the scene cache off a single vertex would leave the
        // rendered edges stale until commit.
        ...(overlay.vertexPreview.vertices
          ? overlay.vertexPreview.vertices.map(vertex => `${vertex.vertexId}:${vertex.x},${vertex.y}`)
          : [`${overlay.vertexPreview.vertexId}:${overlay.vertexPreview.x},${overlay.vertexPreview.y}`]),
        ...(overlay.vertexPreview.handles ?? []).map(handle => `${handle.edgeId}:${handle.endpoint}:${handle.x},${handle.y}`),
      ].join(",")
    : "";
  const handlePreview = overlay.handlePreview
    ? `${overlay.handlePreview.nodeId}:${overlay.handlePreview.edgeId}:${overlay.handlePreview.x},${overlay.handlePreview.y}`
    : "";
  const edgeBendPreview = overlay.edgeBendPreview
    ? `${overlay.edgeBendPreview.nodeId}:${overlay.edgeBendPreview.edgeId}:${overlay.edgeBendPreview.fromHandle.x},${overlay.edgeBendPreview.fromHandle.y},${overlay.edgeBendPreview.toHandle.x},${overlay.edgeBendPreview.toHandle.y}`
    : "";
  const topologyPreview = overlay.vectorTopologyPreview
    ? [
        overlay.vectorTopologyPreview.nodeId,
        overlay.vectorTopologyPreview.network.vertices.length,
        overlay.vectorTopologyPreview.network.edges.length,
        ...overlay.vectorTopologyPreview.newVertexIds.map(vertexId => {
          const vertex = overlay.vectorTopologyPreview!.network.vertices.find(candidate => candidate.id === vertexId);
          return vertex ? `${vertexId}:${vertex.x},${vertex.y}` : vertexId;
        }),
        ...overlay.vectorTopologyPreview.network.edges
          .filter(edge =>
            overlay.vectorTopologyPreview!.newVertexIds.includes(edge.from.vertexId)
            || overlay.vectorTopologyPreview!.newVertexIds.includes(edge.to.vertexId),
          )
          .map(edge => `${edge.id}:${edge.from.vertexId}-${edge.to.vertexId}`),
      ].join(",")
    : "";
  const draftShape = overlay.draftShape
    ? `${overlay.draftShape.kind}:${overlay.draftShape.bounds.x},${overlay.draftShape.bounds.y},${overlay.draftShape.bounds.width},${overlay.draftShape.bounds.height}`
    : "";
  const draftVector = overlay.draftVectorNetwork
    ? draftVectorNetworkSignature(overlay.draftVectorNetwork.node)
    : "";
  const draftComponent = overlay.draftComponentInstance
    ? `${overlay.draftComponentInstance.node.id}:${overlay.draftComponentInstance.node.bounds.x},${overlay.draftComponentInstance.node.bounds.y},${overlay.draftComponentInstance.node.bounds.width},${overlay.draftComponentInstance.node.bounds.height}`
    : "";
  const textPreviews = overlay.textPreviews
    ? [...overlay.textPreviews.entries()].map(([nodeId, text]) => `${nodeId}:${text.style.fontFamily}:${text.style.fontWeight}`).join("|")
    : "";
  const hiddenNodeIds = [...(overlay.hiddenNodeIds ?? [])].sort().join("|");
  const hiddenNodeIdsPreserveOverlayLookup = [...(overlay.hiddenNodeIdsPreserveOverlayLookup ?? [])].sort().join("|");

  return [
    documentRevision,
    overlay.activePageId ?? "",
    hiddenNodeIds,
    hiddenNodeIdsPreserveOverlayLookup,
    movePreviews,
    copyPreviews,
    vectorPreview,
    handlePreview,
    edgeBendPreview,
    topologyPreview,
    draftShape,
    draftVector,
    draftComponent,
    textPreviews,
  ].join(";");
}

export function overlayRequiresFullScene(overlay: OverlaySceneSignatureInput): boolean {
  if ((overlay.hiddenNodeIds?.length ?? 0) > 0 || (overlay.hiddenNodeIdsPreserveOverlayLookup?.length ?? 0) > 0) {
    return true;
  }
  if (overlay.previews.some(preview => preview.nodeId !== "__marquee__")) {
    return true;
  }
  if (overlay.draftShape) {
    return true;
  }
  if (overlay.draftVectorNetwork) {
    return true;
  }
  if (overlay.draftComponentInstance) {
    return true;
  }
  if (overlay.vertexPreview || overlay.handlePreview || overlay.edgeBendPreview || overlay.vectorTopologyPreview) {
    return true;
  }
  if (overlay.textPreviews && overlay.textPreviews.size > 0) {
    return true;
  }
  return false;
}

export function resolveRenderMode(
  overlay: OverlaySceneSignatureInput,
  document: CurvaDocument,
  cached: SceneCompositionKey | undefined,
  tileVariantKey: string | undefined,
  explicitMode?: RenderMode,
): RenderMode {
  if (explicitMode === "full") {
    return "full";
  }
  if (explicitMode === "camera") {
    if (
      !cached
      || cached.document.id !== document.id
      || cached.revision !== document.revision
    ) {
      return "full";
    }
    if (overlayRequiresFullScene(overlay)) {
      return "full";
    }
    const signature = overlaySceneSignature(overlay, document.revision);
    if (cached.sceneSignature !== signature || cached.tileVariantKey !== tileVariantKey) {
      return "full";
    }
    return "camera";
  }
  return overlayRequiresFullScene(overlay) ? "full" : "camera";
}

export function classifyDisplayItem(item: DisplayItem, dynamicItemIds: Set<string>): DisplayRenderClass {
  if (dynamicItemIds.has(item.id)) {
    return "dynamic";
  }
  if ((item.effects ?? []).some(effect => effect.visible && effect.type === "backgroundBlur" && effect.blur > 0)) {
    return "liveBackdrop";
  }
  // Static translucent content is safe to bake into a tile in document paint
  // order. Keeping it live makes every camera frame redraw the entire scene.
  return "tileable";
}

export function buildPaintOrderItems(
  renderedItems: DisplayItem[],
  copyPreviewItems: DisplayItem[],
): DisplayItem[] {
  if (copyPreviewItems.length === 0) {
    return renderedItems;
  }

  const copyById = new Map(copyPreviewItems.map(item => [item.id, item]));
  const result: DisplayItem[] = [];
  for (const item of renderedItems) {
    result.push(item);
    const copyItem = copyById.get(item.id);
    if (copyItem) {
      result.push(copyItem);
    }
  }
  return result;
}

export function requiresPaintOrderLiveCompose(
  dynamicItemCount: number,
  _copyPreviewItemCount: number,
  liveBackdropItemCount = 0,
): boolean {
  // Copy ghosts are drawn live on top of tiles (see composeSceneFrame liveItems).
  // Forcing full paint-order live compose for copies alone re-draws the entire
  // document every Alt-drag pointer move — catastrophic on large docs.
  // Move previews still need live compose because originals leave the tile set.
  return dynamicItemCount > 0 || liveBackdropItemCount > 0;
}

export function tileRasterItemsInPaintOrder(
  paintOrderItems: DisplayItem[],
  dynamicItemIds: Set<string>,
  includeLiveBackdrop: boolean,
): DisplayItem[] {
  return paintOrderItems.filter(item => {
    const renderClass = classifyDisplayItem(item, dynamicItemIds);
    if (renderClass === "tileable") {
      return true;
    }
    return includeLiveBackdrop && renderClass === "liveBackdrop";
  });
}

export function splitItemsByRenderClass(
  items: DisplayItem[],
  dynamicItemIds: Set<string>,
): Record<DisplayRenderClass, DisplayItem[]> {
  const result: Record<DisplayRenderClass, DisplayItem[]> = {
    tileable: [],
    liveBackdrop: [],
    liveRaster: [],
    dynamic: [],
  };
  for (const item of items) {
    result[classifyDisplayItem(item, dynamicItemIds)].push(item);
  }
  return result;
}

export function sceneCompositionKeysEqual(left: SceneCompositionKey | undefined, right: SceneCompositionKey | undefined): boolean {
  if (!left || !right) {
    return false;
  }
  return left.document.id === right.document.id &&
    left.revision === right.revision &&
    left.activePageId === right.activePageId &&
    left.sceneSignature === right.sceneSignature &&
    left.tileVariantKey === right.tileVariantKey;
}
